import { useState } from "react";
import { Link2, Plus, ExternalLink, Trash2, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { useTenantStore } from "@/stores/useTenantStore";
import {
  useCreateOffsite,
  useDeleteOffsite,
  useOffsite,
  useUpdateOffsite,
} from "@/hooks/usePlanner";
import type { OffsiteStatus } from "@/services/plannerService";

const STATUSES: OffsiteStatus[] = ["planned", "live", "lost"];
const STATUS_STYLE: Record<OffsiteStatus, string> = {
  planned: "bg-coral-light text-coral-dark",
  live: "bg-teal-light text-teal-dark",
  lost: "bg-destructive/10 text-destructive",
};

export default function PlannerOffsiteLinksPage() {
  const { currentTenant } = useTenantStore();
  const { data: links = [], isLoading } = useOffsite(currentTenant);
  const createLink = useCreateOffsite(currentTenant);
  const updateLink = useUpdateOffsite();
  const deleteLink = useDeleteOffsite();

  const [url, setUrl] = useState("");
  const [anchor, setAnchor] = useState("");
  const [target, setTarget] = useState("");

  const add = () => {
    if (!url.trim()) return;
    createLink.mutate(
      { url: url.trim(), anchor: anchor.trim(), target_url: target.trim() },
      { onSuccess: () => { setUrl(""); setAnchor(""); setTarget(""); } },
    );
  };

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center gap-3">
        <Link2 className="h-6 w-6 text-coral" />
        <div>
          <h1 className="text-xl font-bold tracking-tight text-foreground">Offsite Links</h1>
          <p className="text-sm text-muted-foreground">
            {currentTenant || "Select a client"} — link-building placements and their status.
          </p>
        </div>
      </div>

      <Card className="border-0 shadow-sm">
        {/* Add row */}
        <div className="flex flex-wrap items-end gap-2 border-b border-border bg-muted/30 px-4 py-3">
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Link URL</label>
            <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://site.com/article" className="h-9 w-64" />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Anchor text</label>
            <Input value={anchor} onChange={(e) => setAnchor(e.target.value)} placeholder="water heater repair" className="h-9 w-48" />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Target page</label>
            <Input value={target} onChange={(e) => setTarget(e.target.value)} placeholder="/services" className="h-9 w-44" />
          </div>
          <Button onClick={add} disabled={!url.trim() || !currentTenant || createLink.isPending} className="h-9">
            <Plus className="mr-1 h-4 w-4" /> Add link
          </Button>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/20">
                <TableHead className="min-w-[260px]">Link</TableHead>
                <TableHead className="min-w-[160px]">Anchor</TableHead>
                <TableHead className="min-w-[140px]">Target</TableHead>
                <TableHead className="w-[130px]">Status</TableHead>
                <TableHead className="w-[60px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={5} className="py-10 text-center text-muted-foreground">
                    <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading links…
                  </TableCell>
                </TableRow>
              ) : links.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="py-10 text-center text-sm text-muted-foreground">
                    No offsite links yet — add your first above.
                  </TableCell>
                </TableRow>
              ) : (
                links.map((l) => (
                  <TableRow key={l.id}>
                    <TableCell className="font-medium">
                      <a href={l.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-coral hover:underline">
                        <span className="max-w-[280px] truncate">{l.url}</span>
                        <ExternalLink className="h-3.5 w-3.5 flex-none" />
                      </a>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{l.anchor || "—"}</TableCell>
                    <TableCell className="text-muted-foreground">{l.target_url || "—"}</TableCell>
                    <TableCell>
                      <Select
                        value={l.status}
                        onValueChange={(v) => updateLink.mutate({ id: l.id, body: { status: v as OffsiteStatus } })}
                      >
                        <SelectTrigger className={cn("h-8 w-[104px] border-0 font-semibold capitalize", STATUS_STYLE[l.status])}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        onClick={() => deleteLink.mutate(l.id)}
                        aria-label="Remove link"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
